import React, { useEffect, useRef } from 'react'

import { DeviceType, useDeviceType } from '@library/dom'

import {
  Box,
  Color,
  Display,
  Display2,
  FontWeight,
  Link,
  LinkVariant,
  Space,
  TextAlign,
  Visible,
} from '@gousto-internal/citrus-react'
import { CUSTOMER_REVIEWS } from '../../config'
import chickpeasBackground from '../../assets/CustomerReviewsSectionAssets/chickpeas.png'
import { ReviewsCarousel } from './components/ReviewsCarousel'
import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../tracking'

type TrustPilotType = {
  loadFromElement: (element: HTMLElement | null, force: boolean) => void
}

export type TrustpilotWindow = typeof window & {
  Trustpilot: TrustPilotType
}

export function CustomerReviewsSection() {
  const ref = useRef(null)

  const deviceType = useDeviceType()
  const isMobileDevice = deviceType === DeviceType.MOBILE

  useEffect(() => {
    const win = window as TrustpilotWindow
    // widget may not load if the bootstrapper script has been loaded before the component gets rendered
    // we use the TrustPilot loadFromElement function to manually load the widget if this happens

    if (win.Trustpilot) {
      win.Trustpilot.loadFromElement(ref.current, true)
    }
  }, [])

  const trackClickTrustpilotCTA = () => {
    trackCTAClick(
      TrackingActions.CUSTOMER_REVIEWS_TRUSTPILOT_CTA,
      LandingPageContentSections.CUSTOMER_REVIEWS,
    )
  }

  return (
    <section data-testid="customerReviewsSection">
      <Box
        bg={Color.NeutralGrey_50}
        style={{
          backgroundImage: `url(${chickpeasBackground})`,
          backgroundRepeat: 'no-repeat',
          paddingLeft: isMobileDevice ? '16px' : 0,
          paddingTop: '44px',
        }}
        paddingBottom={6}
      >
        <Visible display={[Display.Block, Display.None]}>
          <Box height="20px" />
        </Visible>
        <Display2
          data-testid="customer-reviews-header"
          size={[11, 13]}
          fontWeight={FontWeight.Bold}
          textAlign={[TextAlign.Left, TextAlign.Center]}
          style={{ paddingLeft: isMobileDevice ? '8px' : 0 }}
        >
          <Visible display={[Display.Block, Display.None]}>
            {CUSTOMER_REVIEWS.headerMobileLine1}
            <br />
            {CUSTOMER_REVIEWS.headerMobileLine2}
          </Visible>
          <Visible display={[Display.None, Display.Block]}>
            {CUSTOMER_REVIEWS.headerTabletAndDesktop}
          </Visible>
        </Display2>
        <Space size={4} />
        <ReviewsCarousel />
        <Space size={5} />
        <Box style={{ position: 'relative' }}>
          <Box
            ref={ref}
            data-testid="trustpilot-widget"
            data-locale="en-GB"
            data-template-id="5419b6a8b0d04a076446a9ad"
            data-businessunit-id="512cda6e000064000522fb6a"
            data-style-height="27px"
            data-style-width="100%"
            data-theme="light"
            data-review-languages="en"
            data-font-family="Montserrat"
            color={Color.ColdGrey_800}
            style={{ fontSize: '16px', lineHeight: '24px' }}
          />
          <Link
            data-testid="trustpilot-link"
            href="https://uk.trustpilot.com/review/gousto.co.uk"
            target="_blank"
            rel="noopener"
            onClick={trackClickTrustpilotCTA}
            aria-label="Open Gousto reviews on Trustpilot"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'block',
            }}
            variant={LinkVariant.None}
          />
        </Box>
      </Box>
    </section>
  )
}
